/**
 * User Service
 * Handles user-related API calls
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';
import { UserData, SecurityPolicies } from '../utils/tokenManager';

export interface UpdateProfileRequest {
  firstName: string;
  lastName: string;
  email: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Get current user profile
 */
export const getUserProfile = async (): Promise<UserData> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.USERS.ME);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Update user profile
 */
export const updateUserProfile = async (data: UpdateProfileRequest): Promise<UserData> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.USERS.UPDATE_PROFILE, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Change password
 */
export const changePassword = async (data: ChangePasswordRequest): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }
};

/**
 * Get enforced security policies for current user
 */
export const getEnforcedPolicies = async (): Promise<SecurityPolicies> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.USERS.POLICIES);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};
